import { computeUtilization, parseRawAmount } from '../../../../utils/parsing'
import { exchangeRateAccumulator } from '../../accumulator'
import { LENDER_MODE_NO_MODE } from '../../../../assets'
import { createMarketUid } from '../../../../utils'
import { lenderShortName } from '../../../../utils/marketName'
import { parseTakaraMarketsInfo, getTakaraRates } from '../getters/takara'
import {
  PRICE_DECIMALS,
  RESERVE_MANTISSA_DECIMALS,
  ConvertEntryOpts,
  resolveMarketPrice,
} from './types'
import { DEFAULT_CLOSE_FACTOR, DEFAULT_LIQUIDATION_PENALTY } from './risk'

// TAKARA returns all markets in a single getActiveMarketsInfo() call.
// On-chain values (tvl, totalSupply, totalBorrows) are already price-scaled
// (multiplied by oracle price / 10^decimals), so we use off-chain prices
// to derive token-denominated amounts.
export function convertTakaraEntry(opts: ConvertEntryOpts) {
  const { entryRaw, asset, chainId, lender, prices, additionalYields, cToken } =
    opts

  const info = parseTakaraMarketsInfo(entryRaw)
  const rates = getTakaraRates(info)
  const price = resolveMarketPrice(prices, asset, chainId)

  const collateralFactor = Number(
    parseRawAmount(info.ltv.toString(), RESERVE_MANTISSA_DECIMALS),
  )

  // On-chain values are price-scaled (oracle price / 10^decimals).
  // Parse them as 18-decimal values to get a USD-ish number.
  const totalSupplyUSD = Number(
    parseRawAmount(info.totalSupply.toString(), PRICE_DECIMALS),
  )
  const totalDebtUSD = Number(
    parseRawAmount(info.totalBorrows.toString(), PRICE_DECIMALS),
  )
  const liquidityUSD = Number(
    parseRawAmount(info.tvl.toString(), PRICE_DECIMALS),
  )

  // Derive token-denominated amounts from off-chain price
  const totalDeposits = price > 0 ? totalSupplyUSD / price : 0
  const totalDebt = price > 0 ? totalDebtUSD / price : 0
  const liquidity = price > 0 ? liquidityUSD / price : 0

  const poolId = asset.address
  const marketUid = createMarketUid(chainId, lender, cToken)

  return {
    marketUid,
    name: lenderShortName(lender) + ' ' + (asset?.symbol ?? ''),
    poolId,
    asset,
    accumulator: exchangeRateAccumulator(info.exchangeRateCurrent),
    depositRate: rates.depositRate,
    variableBorrowRate: rates.variableBorrowRate,
    stableBorrowRate: 0,
    totalLiquidity: liquidity,
    borrowLiquidity: liquidity,
    totalLiquidityUSD: liquidityUSD,
    borrowLiquidityUSD: liquidityUSD,
    totalDebt,
    totalDebtUSD: totalDebtUSD,
    totalDeposits,
    totalDepositsUSD: totalSupplyUSD,
    totalDebtStable: 0,
    totalDebtStableUSD: 0,
    // Token-denominated amounts depend on an off-chain price; the
    // USD-scaled values from the lens are independent of it, so derive
    // utilization from those to avoid divide-by-zero when the price feed
    // is missing.
    utilization: computeUtilization(totalSupplyUSD, totalDebtUSD),
    intrinsicYield: additionalYields?.intrinsicYields?.[asset.assetGroup] ?? 0,
    config: {
      [LENDER_MODE_NO_MODE]: {
        category: LENDER_MODE_NO_MODE,
        borrowCollateralFactor: collateralFactor,
        collateralFactor,
        borrowFactor: 1,
        liquidationPenalty: DEFAULT_LIQUIDATION_PENALTY,
        closeFactor: DEFAULT_CLOSE_FACTOR,
        collateralDisabled: false,
        debtDisabled: false,
      },
    },
    closeFactor: DEFAULT_CLOSE_FACTOR,
    hasStable: false,
    underlying: asset.address,
    isActive: info.isListed,
    isFrozen: false,
    rewards: [],
    collateralActive: info.isListed,
    borrowingEnabled: info.isListed,
    depositsEnabled: true,
    params: {
      metadata: {
        cToken,
        exchangeRate: info.exchangeRateCurrent.toString(),
        cTokenDecimals: 8,
      },
    },
  }
}
